import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { MeLoginHistoryItem, MeProfile, MeSession } from '../api/types';
import { useAuthStore } from '../stores/authStore';
import { useUIStore, type Theme } from '../stores/uiStore';

export const meKeys = {
  profile: ['me'] as const,
  sessions: ['me', 'sessions'] as const,
  loginHistory: ['me', 'login-history'] as const,
};

// Sync the server-side theme preference (`users.theme`) into the live
// runtime store after each /me resolution. Server is authoritative;
// the manual toggle in `useThemeToggle` writes back via PATCH /me so
// subsequent /me refetches echo the same value (no flap, no loop —
// `setTheme` is a zustand setter and does not invalidate the query).
function applyServerTheme(serverTheme: string): void {
  const valid: Theme | null =
    serverTheme === 'light' || serverTheme === 'dark' ? serverTheme : null;
  if (!valid) return;
  if (useUIStore.getState().theme !== valid) {
    useUIStore.getState().setTheme(valid);
  }
}

export function useMeQuery(enabled = true) {
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: meKeys.profile,
    queryFn: async (): Promise<MeProfile> => {
      const res = await apiClient.get<MeProfile>('/me');
      setUser({
        id: res.data.id,
        email: res.data.email,
        name: res.data.name,
        orgId: res.data.orgId,
        isSuperUser: res.data.isSuperUser,
      });
      applyServerTheme(res.data.theme);
      return res.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Manual theme toggle that BOTH updates the live store and persists the
// new preference to the server. Without the PATCH, a /me refetch would
// yank the user back to whatever the database row says — the exact
// "flap" the FIX MODE prompt called out. Header now uses this instead
// of calling `useUIStore.toggleTheme()` directly.
export function useThemeToggle() {
  const updateMe = useUpdateMe();
  const theme = useUIStore((s) => s.theme);
  return {
    theme,
    isPending: updateMe.isPending,
    toggle: (): void => {
      const next: Theme = theme === 'dark' ? 'light' : 'dark';
      useUIStore.getState().setTheme(next);
      // fire-and-forget: localStorage already holds the new value, so a
      // server-side failure does not break the UI; a follow-up /me will
      // overwrite the local store with the server's value (which is
      // the correct semantics if the PATCH was rejected).
      updateMe.mutate({ theme: next });
    },
  };
}

// PROFILE-SLIMDOWN — `useUpdateMe` is now exclusively used by
// `useThemeToggle` to persist the theme preference. The backend route
// rejects any other field via a strict Zod schema, so narrowing the
// input type here aligns the client contract with the server one and
// prevents stale call sites from being added that the server would
// just reject anyway.
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { theme: 'dark' | 'light' }) => {
      const res = await apiClient.patch<MeProfile>('/me', input);
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(meKeys.profile, data);
      // Without this sync the React Query cache would update but the
      // live `<html data-theme>` + uiStore would not — so the header
      // theme toggle wouldn't visually apply until the next page
      // reload. `applyServerTheme` guards on a value change, so a
      // PATCH whose theme matches what's already live is a no-op.
      // setTheme is a zustand setter — it does not invalidate any
      // query, so no refetch loop is created.
      applyServerTheme(data.theme);
    },
  });
}

// Spec/02.2 line 65 — "Success: toast + force re-login on other
// sessions". The backend now revokes all OTHER sessions on success
// (preserving the current device via the `sid` claim). For legacy
// tokens (no sid) the backend revokes ALL sessions including current
// and signals it with `currentSessionRevoked: true` so the UI can
// render truthful copy.
export interface ChangePasswordResult {
  success: true;
  otherSessionsRevokedCount: number;
  currentSessionRevoked: boolean;
}

export function useChangeMyPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      currentPassword: string;
      newPassword: string;
    }): Promise<ChangePasswordResult> => {
      const res = await apiClient.patch<ChangePasswordResult>(
        '/me/password',
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      // The sessions list is no longer accurate after the backend
      // revoked other rows; force a refetch when the user lands on
      // the Sessions tab.
      qc.invalidateQueries({ queryKey: meKeys.sessions });
    },
  });
}

export function useMySessions() {
  return useQuery({
    queryKey: meKeys.sessions,
    queryFn: async (): Promise<MeSession[]> => {
      const res = await apiClient.get<{ sessions: MeSession[] }>('/me/sessions');
      return res.data.sessions;
    },
    staleTime: 60 * 1000,
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await apiClient.delete(`/me/sessions/${sessionId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meKeys.sessions });
    },
  });
}

// Spec/02.2 (WS-3f). Revokes every session for this user EXCEPT the
// current one. The backend identifies the current session via the
// access token's `sid` claim and returns 400 `SESSION_ID_MISSING` for
// legacy-shaped tokens — the caller must surface that as "sign in
// again to use this action" rather than retrying.
export function useRevokeAllOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/me/sessions');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meKeys.sessions });
    },
  });
}

// Onboarding wizard / checklist intentionally omitted from v1 (product
// decision 2026-05-28). The associated client hooks (useSetupStatus,
// useDismissOnboarding, SetupStatusDto, setupStatusKey) were removed.
// The `users.onboarding_complete` column + `MeProfile.onboardingComplete`
// field remain on the wire for forward compatibility but no UI reads them.

// Spec/02.2 — self login history (B8). Server enforces last 50; no
// pagination parameters are passed because the route ignores them.
export function useMyLoginHistory() {
  return useQuery({
    queryKey: meKeys.loginHistory,
    queryFn: async (): Promise<MeLoginHistoryItem[]> => {
      const res = await apiClient.get<{ items: MeLoginHistoryItem[] }>(
        '/me/login-history',
      );
      return res.data.items;
    },
    staleTime: 60 * 1000,
  });
}
